/**
 * SupplierBookingsPage — /dashboard/supplier/bookings
 *
 * Unified confirmed-work feed. Merges platform bookings (source=quote /
 * instant / classic) with external bookings (supplier self-entered) into
 * one chronological list. The supplier doesn't care where work came from
 * — just that it's confirmed.
 *
 * Tabs: Upcoming · Past · All (counts in pill badges).
 * Each row: date + title + customer + venue + time window + services +
 * a source pill (Platform / External / Quote / Instant).
 * Platform rows link to the chat; external rows link to the calendar day.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle, Lightning, Calendar, MapPin, Clock, EnvelopeSimple, Phone, ChatCircle, PaperPlaneTilt, Copy, X } from '@phosphor-icons/react';
import SupplierBookingLinkModal from '../components/SupplierBookingLinkModal';
import SupplierExternalBookingModal from '../components/SupplierExternalBookingModal';
import SupplierBookingDetailModal from '../components/SupplierBookingDetailModal';
import SupplierSignatureCard from '../components/SupplierSignatureCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_LABEL = {
  dj: 'DJ / Band', photographer: 'Photographer', videographer: 'Videographer',
  photobooth: 'Photo Booth', caterer: 'Caterer', florist: 'Florist',
  venue: 'Venue', cake_maker: 'Cake Maker', transport: 'Transport',
  decorator: 'Decorator', wedding_planner: 'Wedding Planner', hair_makeup: 'Hair & Makeup',
};

const TABS = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'past', label: 'Past' },
  { id: 'all', label: 'All' },
];

const SupplierBookingsPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('upcoming');
  const [state, setState] = useState({ loading: true, bookings: [], counts: { total: 0, platform: 0, external: 0 } });
  // Booking-links ledger — quiet rows showing every link the
  // supplier has sent, with their live status. Loaded once and
  // refreshed after a new link is composed.
  const [links, setLinks] = useState([]);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [externalModalOpen, setExternalModalOpen] = useState(false);
  // Detail modal opens when the supplier clicks a row. We track the
  // booking_link_id so the modal can PATCH/DELETE through the
  // canonical /studio/bookings/{id} endpoint. Platform bookings
  // (chat-linked) keep their existing chat-route behaviour.
  const [detailLinkId, setDetailLinkId] = useState(null);

  const refreshLinks = () => {
    axios.get(`${API}/studio/booking-links`)
      .then((r) => setLinks(r.data?.links || []))
      .catch(() => setLinks([]));
  };
  useEffect(() => { refreshLinks(); }, []);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    axios.get(`${API}/suppliers/me/bookings?status=${tab}`)
      .then((r) => {
        if (cancelled) return;
        setState({ loading: false, bookings: r.data.bookings || [], counts: r.data.counts || {} });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, bookings: [], counts: {} }); });
    return () => { cancelled = true; };
  }, [tab]);

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="supplier-bookings-page">
      <div className="max-w-3xl mx-auto px-5 py-6">
        <button
          onClick={() => navigate('/dashboard/supplier')}
          className="text-white/50 hover:text-white text-sm flex items-center gap-1.5 mb-4"
          data-testid="bookings-back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Dashboard
        </button>

        <div className="mb-5">
          <h1 className="text-3xl font-black tracking-tight">Bookings</h1>
          <p className="text-white/50 text-sm mt-1">
            Confirmed work · platform &amp; external, in one place
          </p>
          <button
            onClick={() => setLinkModalOpen(true)}
            data-testid="open-booking-link-modal"
            className="mt-4 px-4 py-2.5 rounded-full text-sm font-bold flex items-center gap-2"
            style={{
              background: 'linear-gradient(135deg, rgba(194,157,82,0.25), rgba(194,157,82,0.1))',
              border: '1px solid rgba(194,157,82,0.45)',
              color: '#F4E9D2', fontStyle: 'italic',
              fontFamily: 'Georgia, serif',
            }}
          >
            <PaperPlaneTilt size={14} weight="fill" /> Send Booking Link ✦
          </button>
          <button
            onClick={() => setExternalModalOpen(true)}
            data-testid="open-external-booking-modal"
            className="mt-3 ml-2 px-4 py-2.5 rounded-full text-sm font-bold inline-flex items-center gap-2"
            style={{
              background: 'transparent',
              border: '1px solid rgba(194,157,82,0.35)',
              color: '#F4E9D2',
              fontFamily: 'Georgia, serif',
            }}
          >
            ✦ Create External Booking
          </button>
        </div>

        {/* Tab pills */}
        <div className="flex gap-1.5 mb-5" data-testid="bookings-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${
                tab === t.id
                  ? 'bg-[#B89762]/30/15 text-[#F4E9D2] border-[#B89762]/40/40'
                  : 'bg-white/[0.04] text-white/60 border-white/10 hover:bg-white/[0.08]'
              }`}
              data-testid={`bookings-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Source split strip */}
        {state.counts?.total > 0 && (
          <div className="flex gap-2 mb-4 text-[11px]" data-testid="bookings-split">
            <span className="px-2.5 py-1 rounded-full bg-emerald-400/10 border border-emerald-400/20 text-emerald-200 font-bold">
              {state.counts.platform || 0} platform
            </span>
            <span className="px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/60 font-bold">
              {state.counts.external || 0} external
            </span>
          </div>
        )}

        {state.loading ? (
          <p className="text-white/40 text-sm text-center py-10">Loading…</p>
        ) : state.bookings.length === 0 ? (
          <EmptyState tab={tab} onCalendar={() => navigate('/dashboard/supplier/calendar')} />
        ) : (
          <ul className="space-y-3" data-testid="bookings-list">
            {state.bookings.map((b) => (
              <BookingRow
                key={`${b.kind}-${b.id}`}
                booking={b}
                onOpenChat={() => navigate(`/chat/${b.chat_id}`)}
                onOpenDetail={() => {
                  // External bookings created via the booking-link
                  // pathway carry booking_link_id; non-linked rows
                  // fall back to opening the calendar so the
                  // supplier can still see the day in context.
                  if (b.booking_link_id) {
                    setDetailLinkId(b.booking_link_id);
                  } else {
                    navigate('/dashboard/supplier/calendar');
                  }
                }}
              />
            ))}
          </ul>
        )}

        {/* Booking Links ledger — quiet rows, no analytics styling */}
        <BookingLinksLedger
          links={links}
          onCompose={() => setLinkModalOpen(true)}
          onRefresh={refreshLinks}
        />

        {/* Supplier signature — a one-shot mark every confirmation
            PDF carries above the gold footer rule. Makes the
            confirmation feel "real" — clients trust it more, the
            supplier can forward it confidently. */}
        <SupplierSignatureCard />
      </div>

      <SupplierBookingLinkModal
        open={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        onCreated={() => { refreshLinks(); }}
      />
      <SupplierExternalBookingModal
        open={externalModalOpen}
        onClose={() => setExternalModalOpen(false)}
        onCreated={() => {
          refreshLinks();
          // refresh the bookings tab too — a new external row just landed
          axios.get(`${API}/suppliers/me/bookings?status=${tab}`).then((r) => {
            setState({ loading: false, bookings: r.data.bookings || [], counts: r.data.counts || {} });
          }).catch(() => {});
        }}
      />
      <SupplierBookingDetailModal
        open={Boolean(detailLinkId)}
        linkId={detailLinkId}
        onClose={() => setDetailLinkId(null)}
        onChanged={() => {
          // Booking edited — re-pull both ledgers so the new copy
          // appears immediately.
          refreshLinks();
          axios.get(`${API}/suppliers/me/bookings?status=${tab}`).then((r) => {
            setState({ loading: false, bookings: r.data.bookings || [], counts: r.data.counts || {} });
          }).catch(() => {});
        }}
        onDeleted={() => {
          refreshLinks();
          axios.get(`${API}/suppliers/me/bookings?status=${tab}`).then((r) => {
            setState({ loading: false, bookings: r.data.bookings || [], counts: r.data.counts || {} });
          }).catch(() => {});
        }}
      />
    </div>
  );
};

const EmptyState = ({ tab, onCalendar }) => (
  <div className="text-center py-12 px-6 rounded-2xl bg-white/[0.02] border border-white/5" data-testid="bookings-empty">
    <CheckCircle size={32} weight="duotone" className="mx-auto text-[#C29D52]/50 mb-3" />
    <p className="font-bold text-white mb-1">
      {tab === 'upcoming' ? 'No upcoming bookings yet' : tab === 'past' ? 'No past bookings' : 'No bookings yet'}
    </p>
    <p className="text-white/50 text-xs mb-5">
      {tab === 'past'
        ? 'Your completed work will appear here.'
        : 'Confirmed enquiries and external bookings land here automatically.'}
    </p>
    <button
      onClick={onCalendar}
      className="py-2.5 px-5 rounded-lg text-xs font-bold text-white"
      style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
      data-testid="bookings-empty-calendar-btn"
    >
      Open calendar
    </button>
  </div>
);

const BookingRow = ({ booking: b, onOpenChat, onOpenDetail }) => {
  const isPlatform = b.kind === 'platform';
  const d = b.event_date ? new Date(b.event_date) : null;
  const dateLabel = d
    ? d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    : 'Date TBC';
  const timeLabel = b.start_time || b.end_time ? `${b.start_time || '—'}–${b.end_time || '—'}` : null;
  const services = (b.services_required || []).map((s) => SERVICE_LABEL[s] || s).join(' · ');
  const sourceBadge = isPlatform
    ? (b.source === 'quote' ? { label: 'Quote', cls: 'bg-[#B89762]/40/15 text-[#C29D52]/85 border-[#B89762]/40/30' }
       : b.source === 'instant' ? { label: 'Instant', cls: 'bg-[#B89762]/40/15 text-[#C29D52]/85 border-[#B89762]/40/30' }
       : { label: 'Platform', cls: 'bg-emerald-400/15 text-emerald-200 border-emerald-400/30' })
    : { label: 'External', cls: 'bg-white/[0.06] text-white/70 border-white/15' };

  return (
    <li
      className="rounded-xl bg-white/[0.03] border border-white/10 p-4 cursor-pointer hover:bg-white/[0.05] transition-colors"
      onClick={isPlatform ? onOpenChat : onOpenDetail}
      data-testid={`booking-row-${b.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5 text-[10px] font-black uppercase tracking-wider text-[#C29D52]">
            <Calendar size={11} weight="fill" />
            {dateLabel}
          </div>
          <p className="font-black text-base truncate">{b.title}</p>
          {b.customer_name && (
            <p className="text-white/60 text-xs mt-0.5 truncate">{b.customer_name}</p>
          )}
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${sourceBadge.cls}`}>
          {sourceBadge.label}
        </span>
      </div>

      <div className="space-y-1 text-[11px] text-white/65">
        {b.venue && (
          <p className="flex items-start gap-1.5"><MapPin size={11} weight="fill" className="mt-0.5 shrink-0 text-[#C29D52]/70" /><span className="truncate">{b.venue}</span></p>
        )}
        {timeLabel && (
          <p className="flex items-start gap-1.5"><Clock size={11} weight="fill" className="mt-0.5 shrink-0 text-[#C29D52]/70" />{timeLabel}</p>
        )}
        {services && (
          <p className="flex items-start gap-1.5"><Lightning size={11} weight="fill" className="mt-0.5 shrink-0 text-[#C29D52]/70" /><span className="truncate">{services}</span></p>
        )}
        {b.price != null && (
          <p className="flex items-start gap-1.5 text-emerald-200 font-bold"><CheckCircle size={11} weight="fill" className="mt-0.5 shrink-0" />£{Number(b.price).toLocaleString('en-GB')}{b.duration_hours ? ` · ${b.duration_hours}h` : ''}</p>
        )}
        {/* External-only contact links — visible to supplier because they entered it */}
        {!isPlatform && b.customer_email && (
          <p className="flex items-start gap-1.5" onClick={(e) => e.stopPropagation()}><EnvelopeSimple size={11} weight="fill" className="mt-0.5 shrink-0 text-[#C29D52]/70" /><a href={`mailto:${b.customer_email}`} className="text-[#C29D52]/85 hover:text-[#F4E9D2] truncate">{b.customer_email}</a></p>
        )}
        {!isPlatform && b.customer_phone && (
          <p className="flex items-start gap-1.5" onClick={(e) => e.stopPropagation()}><Phone size={11} weight="fill" className="mt-0.5 shrink-0 text-[#C29D52]/70" /><a href={`tel:${b.customer_phone}`} className="text-[#C29D52]/85 hover:text-[#F4E9D2]">{b.customer_phone}</a></p>
        )}
        {!isPlatform && b.notes && (
          <p className="italic text-white/45 pt-0.5">&ldquo;{b.notes}&rdquo;</p>
        )}
      </div>

      {isPlatform && b.chat_id && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-1.5 text-[11px] text-[#C29D52]/85/80 font-bold">
          <ChatCircle size={11} weight="fill" /> Open chat →
        </div>
      )}
    </li>
  );
};

export default SupplierBookingsPage;

// ────────────────────────────────────────────────────
// Booking Links ✦ ledger
// Quiet rows only — no analytics, no charts.
// ────────────────────────────────────────────────────
const STATUS_TONE = {
  pending:  { label: 'Sent',          cls: 'bg-white/[0.04] text-white/55 border-white/10' },
  viewed:   { label: 'Opened softly', cls: 'bg-[#B89762]/40/10 text-[#C29D52]/85 border-[#B89762]/40/20' },
  linked:   { label: 'Settled',       cls: 'bg-emerald-400/12 text-emerald-200 border-emerald-400/25' },
  accepted: { label: 'Settled',       cls: 'bg-emerald-400/12 text-emerald-200 border-emerald-400/25' },
  expired:  { label: 'Settled away',  cls: 'bg-white/[0.04] text-white/35 border-white/8' },
  revoked:  { label: 'Withdrawn',     cls: 'bg-white/[0.04] text-white/35 border-white/8' },
};

const publicBookUrl = (token) => {
  if (typeof window === 'undefined') return `/book/${token}`;
  return `${window.location.origin}/book/${token}`;
};

const BookingLinksLedger = ({ links, onCompose, onRefresh }) => {
  const [copiedToken, setCopiedToken] = useState(null);

  const handleCopy = async (token) => {
    try {
      await navigator.clipboard.writeText(publicBookUrl(token));
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2200);
    } catch {}
  };

  const handleRevoke = async (linkId) => {
    if (!window.confirm('Withdraw this invitation? The link will stop working.')) return;
    try {
      await axios.post(`${API}/studio/booking-links/${linkId}/revoke`);
      onRefresh && onRefresh();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't withdraw the link");
    }
  };

  return (
    <section className="mt-10" data-testid="booking-links-ledger">
      <div className="flex items-end justify-between mb-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.32em] text-amber-300/70">✦ Booking Links</p>
          <h2 className="font-black text-xl mt-1">Invitations you’ve sent</h2>
        </div>
        {links.length > 0 && (
          <button
            onClick={onCompose}
            data-testid="ledger-compose-cta"
            className="text-[11px] italic text-amber-200/80 hover:text-amber-100"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            + Compose another
          </button>
        )}
      </div>

      {links.length === 0 ? (
        <div className="text-center py-10 px-6 rounded-2xl bg-white/[0.02] border border-white/5" data-testid="booking-links-empty">
          <p className="text-white/55 text-sm italic" style={{ fontFamily: 'Georgia, serif' }}>
            No invitations sent yet. When you send one, it lives quietly here.
          </p>
          <button
            onClick={onCompose}
            data-testid="booking-links-empty-cta"
            className="mt-4 px-4 py-2 rounded-full text-xs italic"
            style={{
              background: 'transparent',
              border: '1px solid rgba(194,157,82,0.45)',
              color: '#F4E9D2', fontFamily: 'Georgia, serif',
            }}
          >
            Send your first booking link ✦
          </button>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="booking-links-list">
          {links.map((l) => {
            const tone = STATUS_TONE[l.status] || STATUS_TONE.pending;
            const isOpen = ['pending', 'viewed'].includes(l.status);
            return (
              <li
                key={l.id}
                className="rounded-xl bg-white/[0.025] border border-white/8 px-4 py-3"
                data-testid={`booking-link-row-${l.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-[14px] truncate" style={{ fontFamily: 'Georgia, serif' }}>
                      {l.client_name || l.package_name}
                    </p>
                    <p className="text-white/45 text-[11px] mt-0.5 truncate">
                      {l.package_name}{l.event_date ? ` · ${l.event_date}` : ''}
                    </p>
                    {/* Origin badge — Requested (client completed the
                        public form) vs. Created quietly (supplier built
                        it on the client's behalf). Quiet typography,
                        no SaaS chrome. */}
                    {l.status === 'linked' && (
                      <p
                        data-testid={`booking-link-origin-${l.id}`}
                        className="text-[10px] mt-1.5"
                        style={{
                          fontFamily: 'Arial, sans-serif',
                          fontWeight: 700,
                          letterSpacing: '0.18em',
                          textTransform: 'uppercase',
                          color: 'rgba(194,157,82,0.7)',
                        }}
                      >
                        {l.booking_origin === 'supplier' ? 'Created quietly' : 'Requested'}
                      </p>
                    )}
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${tone.cls}`}
                        style={{ fontFamily: 'Georgia, serif', fontWeight: 600, letterSpacing: '0.08em' }}>
                    {tone.label}
                  </span>
                </div>
                {isOpen && (
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => handleCopy(l.token)}
                      data-testid={`booking-link-copy-${l.id}`}
                      className="text-[11px] flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10"
                      style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: 'rgba(244,233,210,0.78)' }}
                    >
                      {copiedToken === l.token ? <><CheckCircle size={11} weight="fill" /> Copied</> : <><Copy size={11} /> Copy link</>}
                    </button>
                    <button
                      onClick={() => handleRevoke(l.id)}
                      data-testid={`booking-link-revoke-${l.id}`}
                      className="text-[11px] flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-rose-900/20"
                      style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', color: 'rgba(244,233,210,0.5)' }}
                    >
                      <X size={11} /> Withdraw
                    </button>
                  </div>
                )}
                {l.status === 'linked' && l.linked_event_id && (
                  <p className="text-[11px] mt-1 text-emerald-200/70 italic" style={{ fontFamily: 'Georgia, serif' }}>
                    Linked to {l.linked_event_id.slice(0, 14)}… ✦
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
